<template>
  <ModalQuestions
    :open="questionModalOpen"
    :question="currentQuestion"
    :canClose="false"
    @submit="onQuestionAnswered"
  />

  <ModalStatus />

  <span class="hidden">{{ lessonActive }}</span>
  <div
    v-if="error"
    class="text-center text-white mt-10 tracking-wider p-2 bg-[#d20a0a] uppercase"
  >
    {{ error }}
  </div> 

  <div
    v-else-if="upcoming"
    class="text-center text-white mt-10 tracking-wider p-2 bg-[#2e3094] uppercase"
  >
    ESTA AULA AINDA NÃO ESTA DISPONIVEL

    <Timer
      :initial-hours="timerCount.hours"
      :initial-minutes="timerCount.minutes"
      :initial-seconds="timerCount.seconds"
      @finished="(val) => (start = val)"
    />
  </div>

  <div
    v-else-if="loading"
    class="flex justify-center items-center h-96 tracking-wider p-2 bg-[#374151] uppercase"
  >
    <span class="text-white">Carregando transmissão...</span>
  </div>

  <div v-else class="relative w-full aspect-video" :key="start">
    <div
      :key="iframeKey"
      ref="playerWrapper"
      class="w-full h-full overflow-hidden rounded-xl shadow-lg relative"
      :class="{ fullscreen: isFullscreen }"
    >
      <iframe
        ref="embedRef"
        :key="iframeKey"
        class="w-full h-full"
        :src="youtubeEmbedUrl"
        frameborder="0"
        allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
        allowfullscreen
        sandbox="allow-same-origin allow-scripts allow-presentation"
        @contextmenu.prevent
      ></iframe>

      <div
        class="absolute inset-0 z-10 cursor-default"
        @contextmenu.prevent
      ></div>

      <!-- Overlay inteligente -->
      <div
        v-if="showOverlay"
        class="absolute inset-0 z-20 bg-black/90 flex items-center justify-center"
      >
        <button
          v-if="overlayType === 'paused'"
          @click="togglePlay"
          class="bg-black/70 text-white px-3 py-3 rounded hover:bg-black/80 tracking-wider uppercase my-auto mx-auto border border-white"
        >
          <PhPause v-if="isPlaying" class="w-10 h-10 mx-auto py-1" />
          <PhPlay v-else class="w-10 h-10 mx-auto py-1" />
          Continuar aula
        </button>

        <button
          v-if="overlayType === 'ended' && status === 'success'"
          class="px-3 py-3 rounded my-auto mx-auto"
        >
          <lottie
            :options="successAnimate"
            @animCreated="handleAnimation"
            class="w-full transition-transform duration-500 ease-out"
            :class="animationCompleted ? 'scale-[1.5]' : 'scale-[1]'"
            height="200"
          />
          AULA CONCLUIDA 🎉
        </button>

        <button
          v-if="overlayType === 'ended' && status === 'failed'"
          class="px-3 py-3 rounded my-auto mx-auto"
        >
          <lottie
            :options="animationOptions"
            @animCreated="handleAnimation"
            class="w-full transition-transform duration-500 ease-out"
            :class="animationCompleted ? 'scale-[1.5]' : 'scale-[1]'"
            height="200"
          />
          NÃO APROVADO 😞
        </button>

      </div>

      <!-- Controles -->
      <div class="absolute bottom-1 left-3 z-30 flex gap-2">
        <!-- <button
          class="bg-black/70 text-white px-3 py-2 rounded hover:bg-black/80 tracking-wider flex my-auto mx-auto"
          :disabled="!ready"
          @click="seekBy(-10)"
        >
          <PhSkipBack class="w-5 h-5" />
          10s
        </button> -->

        <button
          class="bg-black/70 text-white px-3 py-3 rounded hover:bg-black/80 tracking-wider uppercase my-auto mx-auto border border-white"
          :disabled="!ready"
          @click="togglePlay"
        >
          <PhPause v-if="isPlaying" class="w-5 h-5" />
          <PhPlay v-else class="w-5 h-5" />
        </button>
        <!-- <button
          class="bg-black/70 text-white px-3 py-2 rounded hover:bg-black/80 tracking-wider flex my-auto mx-auto"
          :disabled="!ready"
          @click="seekBy(10)"
        >
          10s
          <PhSkipForward class="w-5 h-5" />
        </button> -->
      </div>

      <div class="absolute bottom-1 right-3 z-20 flex gap-2">
        <button
          class="bg-black/60 text-white text-2xl p-2 rounded hover:bg-black/80 tracking-wider uppercase"
          @click="toggleFullscreen"
        >
          <span v-if="!isFullscreen">Tela cheia</span>
          <span v-else>Sair tela cheia</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  ref,
  computed,
  onMounted,
  watch,
  onBeforeUnmount,
  watchEffect,
} from "vue";
import SharedService from "@/shared/services/shared";
import router from "@/router";
import { useRoute } from "vue-router";
import { dataShared } from "@/shared/store";
import Timer from "../__partials/Timer.vue";
import { PhPause, PhPlay } from "@phosphor-icons/vue";
import ModalQuestions from "../__partials/ModalQuestions.vue";
import { reactive } from "vue";
import { dataProgress } from "@/shared/store/progress";
import ModalStatus from "../__partials/ModalStatus.vue";
import { dataStatus } from "@/shared/store/status";

const url = process.env.VUE_APP_API_URL;
const embedToken = ref(null);
const loading = ref(true);
const error = ref(null);
const upcoming = ref(false);
const timerCount = ref(null);
const isFullscreen = ref(false);
const playerWrapper = ref(null);
// const iframeKey = ref(0); // usada para forçar reload do iframe
const iframeStarted = ref(true);
const route = useRoute();
const start = ref(false);
// event listners
const embedRef = ref(null);
const ready = ref(false);
const currentTime = ref(0); // controla o tempo do vídeo
const isPlaying = ref(false);
const showOverlay = ref(false);
const overlayType = ref('ended'); // 'paused' | 'ended'
const currentModule = computed(() => dataShared.getters.getActivatedModule);
const moduleActivated = computed(
  () => dataShared.getters.getEventLiveId ?? route.params.mId
);

const activeGroupId = computed(() => {
  if (route.query.group) {
    return Number(route.query.group)
  }

  return dataShared.getters.getGroupContextId ?? null
})

const user = computed(
  () => dataShared.getters.getDataByUserLogged
);

// animation
const successAnimate = ref({
  animationData: require("@/assets/lotties/SuccessLottieAnimation.json"),
  loop: false,
  autoplay: true,
});
const animationOptions = ref({
  animationData: require('@/assets/lotties/FailedLottieAnimation.json'),
  loop: false,
  autoplay: true
})
const animationCompleted = ref(false);
const handleAnimation = (anim) => {
  anim.addEventListener("complete", () => {
    animationCompleted.value = true;
  });
};

// define a aula atual
const lessonActive = computed(() => {
  if (dataShared.getters.getLastLessonActive !== null) {
    return dataShared.getters.getLastLessonActive;
  }

  return (
    dataShared.getters.getActivatedLesson ??
    currentModule.value?.modules[0]?.lessons[0] ??
    null
  );
});

watch(
  [() => dataShared.getters.getLastLessonActive, currentModule],
  ([lastLesson, module]) => {
    if (lastLesson && module) {
      dataShared.dispatch("setLastClassActive", null);
    }
  }
);

// perguntas
const questionModalOpen = ref(false); // modal de pergunta
const currentQuestion = ref(null); // pergunta que está sendo exibida agora
const firedQuestionIds = ref([]); // ids perguntas já disparadas

// fluxo da pontuação
const lessonScore = ref(0); // pontos somados nesta lição
const lessonPassed = ref(false); // aprovado na lição atual
const answers = reactive(Object.create(null)); // answers[questionId] = { optionId, isCorrect, points }
const savedAnswers = new Set();

const questionsIndex = computed(() => {
  const map = Object.create(null);
  for (const q of lessonActive.value?.questions || []) map[q.id] = q;
  return map;
});

const minRequired = computed(() =>
  Number(lessonActive.value?.min_grade_required || 0)
);

const requiredIds = computed(() => {
  // regra: se q.required === true é obrigatória; default = opcional
  return (lessonActive.value?.questions || [])
    .filter((q) => q.required === true)
    .map((q) => q.id);
});

const restartToken = computed(() => dataShared.state.lessonRestartToken);

const iframeKey = computed(() => {
  return `${lessonActive.value?.id}-${restartToken.value}`;
});

const status = computed(() => dataStatus.getters.status) // status da aula -> passou? não?

// reseta ao trocar de lição
watch(
  () => lessonActive.value?.id,
  () => {
    lessonScore.value = 0;
    lessonPassed.value = false;
    for (const k in answers) delete answers[k];
    savedAnswers.clear();
    firedQuestionIds.value = []; // mantém lógica de disparo
    currentQuestion.value = null;
    questionModalOpen.value = false;
  }
);

watchEffect(async () => {
  if (!lessonActive.value) return;
  if (!restartToken.value) return;

  lessonScore.value = 0;
  lessonPassed.value = false;
  savedAnswers.clear();
  firedQuestionIds.value = [];
  currentQuestion.value = null;

  await getEmbedToken();
});

// avaliador
function evaluate(questionId, optionId) {
  const q = questionsIndex.value[questionId];
  if (!q) return { isCorrect: false, points: 0, question: null };

  const opt = (q.options || []).find((o) => o.id === optionId);
  const isCorrect = !!opt?.is_correct;
  const points = isCorrect ? Number(q.points || 0) : 0;
  return { isCorrect, points, question: q };
}

// checagem de aprovação
function recomputePassFlag() {
  const hasAllRequired = requiredIds.value.every((id) => answers[id]);
  lessonPassed.value = hasAllRequired && lessonScore.value >= minRequired.value;
}

// salva no back
async function saveAnswerToBackend({ lessonId, questionId, optionId }) {
  // Proteção contra duplicidade básica
  const key = `${lessonId}:${questionId}`;
  if (savedAnswers.has(key)) return;
  savedAnswers.add(key);

  try {
    const { data: snapshot } = await SharedService.postLessonAnswer(lessonId, {
      question_id: questionId,
      option_id: optionId,
      groupId: activeGroupId.value
    });

    dataProgress.dispatch("mergeSnapshot", snapshot);
  } catch (err) {
    savedAnswers.delete(key);
    console.error("postLessonAnswer failed", err);
  }
}

// envia o token gerado no back para confirmação e carrega o player
const youtubeEmbedUrl = computed(() => {
  if (!embedToken.value) return "";

  return `${url}embed/${embedToken.value}`;
});

// recebe o token
async function getEmbedToken() {
  loading.value = true;

  await SharedService.getLiveById(
    moduleActivated.value,
    lessonActive.value?.video_id
  )
    .then((res) => {
      if (res.data.status == "upcoming") {
        upcoming.value = true;
        timerCount.value = res.data?.starts_in;
      }

      embedToken.value = res.data.embed_token;
      loading.value = false;
    })
    .catch((err) => {
      if (err.response?.status === 403) {
        loading.value = false;
        error.value =
          "Você não tem acesso a esta transmissão. Solicite seu acesso.";
      } else if (err.response?.status === 404) {
        loading.value = false;
        error.value = "Este treinamento não esta mais disponível!";
      } else if (err.response?.status === 401) {
        loading.value = false;
        router.push("/auth");
      } else {
        loading.value = false;
        error.value = "Erro ao carregar o vídeo. Tente novamente mais tarde.";
      }
    });
}

// lessonActive
watch(
  lessonActive,
  (newVal) => {
    getEmbedToken();
    forcePlay();
  },
  { immediate: true }
);

// fullscreen
function toggleFullscreen() {
  const el = playerWrapper.value;
  if (!document.fullscreenElement) {
    el.requestFullscreen().then(() => {
      isFullscreen.value = true;
    });
  } else {
    document.exitFullscreen().then(() => {
      isFullscreen.value = false;
    });
  }
}

// autoplay
function forcePlay() {
  upcoming.value = false;
  iframeStarted.value = true;
  // iframeKey.value++; // força re-render do iframe
}

// comunicação com o blade
function postToEmbed(msg) {
  const w = embedRef.value?.contentWindow;
  if (!w) return;

  let targetOrigin = "*";
  try {
    targetOrigin = new URL(youtubeEmbedUrl.value).origin;
  } catch {}
  w.postMessage(msg, targetOrigin);
}

// actions
function seekBy(sec) {
  postToEmbed({ type: "SEEK_BY", delta: sec });
} // avança|retrocede o tempo do vídeo
function play() {
  postToEmbed({ type: "PLAY" });
  isPlaying.value = true;
  showOverlay.value = false;
}
function pause() {
  postToEmbed({ type: "PAUSE" });
  isPlaying.value = false;
}
function togglePlay() {
  isPlaying.value ? pause() : play();
}

// fluxo das perguntas
function handleTimeUpdate() {
  if (questionModalOpen.value) return;

  const lesson = lessonActive.value;
  if (!lesson || !Array.isArray(lesson.questions) || !lesson.questions.length)
    return;

  const t = currentTime.value;

  const nextQuestion = lesson.questions.find((q) => {
    const alreadyFired = firedQuestionIds.value.includes(q.id);
    return !alreadyFired && t >= q.time_seconds;
  });

  if (!nextQuestion) return;

  // Marca como disparada para não repetir
  firedQuestionIds.value.push(nextQuestion.id);

  // Pausa o vídeo
  pause();

  // Seta a pergunta atual e abre o modal
  currentQuestion.value = nextQuestion;
  questionModalOpen.value = true;
}

// fluxo pós resposta
async function onQuestionAnswered(payload) {
  // payload = { questionId, optionId }
  const questionId = Number(payload?.questionId);
  const optionId = Number(payload?.optionId);

  // 1) avaliar
  const { isCorrect, points } = evaluate(questionId, optionId);

  dataProgress.dispatch("markQuestionResult", {
    lessonId: lessonActive.value.id,
    questionId,
    result: isCorrect ? "correct" : "wrong",
  });

  // 2) se já havia resposta, remove pontos anteriores
  const prev = answers[questionId];
  if (prev) lessonScore.value -= Number(prev.points || 0);

  // 3) registra resposta atual e soma pontos
  answers[questionId] = { optionId, isCorrect, points };
  lessonScore.value += points;

  // 4) revalida aprovação da lição
  recomputePassFlag();

  // 5) envia ao back e MESCLA snapshot no Vuex
  if (lessonActive.value?.id) {
    saveAnswerToBackend({
      lessonId: lessonActive.value.id,
      questionId,
      optionId,
    });
  }

  // 6) fechar modal e continuar
  questionModalOpen.value = false;
  currentQuestion.value = null;
  play();
}

// mensagens event listener
function onMessage(e) {
  const data = e.data || {};
  if (data.type === "READY") ready.value = true;

  if (data.type === "STATE") {
    // 1 = playing, 2 = paused (YouTube codes)
    isPlaying.value = data.state === 1;

    if (data.state == 1) {
      // playing
      isPlaying.value = true;
      showOverlay.value = false;
    } else if (data.state == 2) {
      // paused
      isPlaying.value = false;
      overlayType.value = "paused";
      showOverlay.value = true;
    }

    // finalize
    if (data.state === 0 && lessonActive.value?.id) {
      SharedService.finalizeLesson(lessonActive.value.id, activeGroupId.value)
        .then(({ data: snapshot }) => {
          overlayType.value = "ended";
          showOverlay.value = true;
          
          if (snapshot.lesson?.status == "failed") {
            dataStatus.dispatch("setStatus", "failed");
            dataStatus.dispatch("setModalOpen", true); // modal de nova tentativa
          } else if (snapshot.lesson?.status == "passed") {
            dataStatus.dispatch("setStatus", "success");
            if (snapshot.training?.completed_modules == snapshot.training?.total_modules) // modal de finalização do treinamento
              dataStatus.dispatch("setModalOpen", true);
          }

          dataProgress.dispatch("mergeSnapshot", snapshot);
        })
        .catch((err) => console.log("finalizeLesson failed", err));
    }
  }

  if (data.type === "TIME") {
    currentTime.value = Number(data.currentTime || 0);
    handleTimeUpdate();
  }
}

onBeforeUnmount(() => window.removeEventListener("message", onMessage));

onMounted(() => {
  window.addEventListener("message", onMessage);

  setTimeout(() => {
    const iframe = document.querySelector("iframe");
    if (!iframe?.contentWindow) {
      iframeStarted.value = false;
    }
  }, 3000);

  getEmbedToken();
});
</script>

<style scoped>
iframe {
  pointer-events: auto;
  user-select: none;
}
</style>
